import styled from "styled-components";
import { media } from "../../styles/breakpoints";

const StructureStyled = styled.article`
  width: 100%;
  max-width: 400px; /* Tamaño máximo de la card */
  display: flex;
  flex-direction: column;
  border-radius: ${(props) => props.theme.border.borderRadiusXL};
  background-color: ${(props) => props.theme.colour.background.primary};
  box-shadow: ${(props) => props.theme.shadows.small};
  transition: ${(props) => props.theme.transitions.normal};
  overflow: hidden;
  position: relative;
  height: 100%;

  ${media.tablet`
    max-width: 350px; /* Ligeramente más pequeñas en tablet para 2 columnas */
  `}

  ${media.desktop`
    max-width: 320px; /* Más pequeñas en desktop para 3 columnas */
  `}

  ${media.wide`
    max-width: 300px; /* Aún más pequeñas en wide para 4 columnas */
  `}

  ${media.ultraWide`
    max-width: 280px; /* Mínimo tamaño para 5 columnas */
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${(props) => props.theme.shadows.large};
  }

  .structure {
    &__wrap {
      width: 100%;
      background-color: ${(props) => props.theme.colour.background.primary};
      border-radius: ${(props) => props.theme.border.borderRadiusXL};
      padding: ${(props) => props.theme.spacing.md};
      display: flex;
      flex-direction: column;
      height: 100%;

      ${media.tablet`
        padding: 20px;
      `}

      ${media.desktop`
        padding: 24px;
      `}

      ${media.wide`
        padding: 28px;
      `}
    }

    &__image-container {
      position: relative;
      width: 100%;
      height: 200px;
      overflow: hidden;
      border-radius: ${(props) => props.theme.border.borderRadiusLarge};
      margin-bottom: ${(props) => props.theme.spacing.md};
      background-color: ${(props) => props.theme.colour.state.loading};

      ${media.tablet`
        height: 180px;
        margin-bottom: 20px;
      `}

      ${media.desktop`
        height: 160px;
        margin-bottom: 24px;
      `}

      ${media.wide`
        height: 150px;
        margin-bottom: 28px;
      `}

      ${media.ultraWide`
        height: 140px;
        margin-bottom: 32px;
      `}
    }

    &__image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: ${(props) => props.theme.border.borderRadiusLarge};
      transition: ${(props) => props.theme.transitions.normal};

      &:hover {
        transform: scale(1.05);
      }
    }

    &__title {
      padding: ${(props) => props.theme.spacing.sm} 0;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      font-size: ${(props) => props.theme.fonts.titleFontSizeM};
      font-weight: 700;
      color: ${(props) => props.theme.colour.text.primary};
      margin-bottom: ${(props) => props.theme.spacing.sm};
      line-height: 1.3;

      ${media.tablet`
        font-size: 1.75rem;
        margin-bottom: 16px;
      `}

      ${media.desktop`
        font-size: 1.5rem;
        margin-bottom: 20px;
      `}

      ${media.wide`
        font-size: 1.375rem;
        margin-bottom: 24px;
      `}

      ${media.ultraWide`
        font-size: 1.25rem;
        margin-bottom: 28px;
      `}
    }

    &__info {
      display: flex;
      flex-direction: column;
      gap: ${(props) => props.theme.spacing.sm};
      margin-bottom: ${(props) => props.theme.spacing.md};
      flex-grow: 1;

      ${media.tablet`
        gap: 12px;
        margin-bottom: 20px;
      `}

      ${media.desktop`
        gap: 16px;
        margin-bottom: 24px;
      `}

      ${media.wide`
        gap: 20px;
        margin-bottom: 28px;
      `}

      > li {
        display: flex;
        align-items: center;
        gap: ${(props) => props.theme.spacing.sm};
        font-size: ${(props) => props.theme.fonts.textFontSizeS};
        color: ${(props) => props.theme.colour.text.tertiary};

        ${media.tablet`
          font-size: 1rem;
          gap: 12px;
        `}

        ${media.desktop`
          font-size: 0.95rem;
          gap: 16px;
        `}

        ${media.wide`
          font-size: 0.9rem;
          gap: 20px;
        `}

        > span {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        > svg {
          height: 18px;
          width: 18px;
          color: ${(props) => props.theme.colour.brand.primary};
          flex-shrink: 0;

          ${media.tablet`
            height: 20px;
            width: 20px;
          `}

          ${media.desktop`
            height: 18px;
            width: 18px;
          `}

          ${media.wide`
            height: 16px;
            width: 16px;
          `}

          ${media.ultraWide`
            height: 14px;
            width: 14px;
          `}
        }
      }
    }

    &__buttons {
      display: flex;
      justify-content: space-around;
      gap: ${(props) => props.theme.spacing.sm};
      padding: ${(props) => props.theme.spacing.md} 0 0;
      border-top: 1px solid ${(props) => props.theme.colour.border.light};
      margin-top: auto;

      ${media.tablet`
        gap: 12px;
        padding: 20px 0 0;
      `}

      ${media.desktop`
        gap: 16px;
        padding: 24px 0 0;
      `}

      ${media.wide`
        gap: 20px;
        padding: 28px 0 0;
      `}

      > * {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: ${(props) => props.theme.spacing.sm};
        border-radius: ${(props) => props.theme.border.borderRadiusSmall};
        background-color: ${(props) => props.theme.colour.background.secondary};
        color: ${(props) => props.theme.colour.text.primary};
        transition: ${(props) => props.theme.transitions.fast};
        min-height: 44px;

        ${media.tablet`
          padding: 12px;
          min-height: 48px;
        `}

        ${media.desktop`
          padding: 16px;
          min-height: 52px;
        `}

        ${media.wide`
          padding: 20px;
          min-height: 56px;
        `}

        &:hover {
          background-color: ${(props) =>
            props.theme.colour.interactive.primary};
          color: ${(props) => props.theme.colour.text.inverse};
          transform: translateY(-1px);
        }

        > svg {
          height: 24px;
          width: 24px;

          ${media.tablet`
            height: 26px;
            width: 26px;
          `}

          ${media.desktop`
            height: 28px;
            width: 28px;
          `}

          ${media.wide`
            height: 30px;
            width: 30px;
          `}

          ${media.ultraWide`
            height: 32px;
            width: 32px;
          `}
        }
      }
    }

    /* Badge de estado */
    &__status {
      position: absolute;
      top: ${(props) => props.theme.spacing.sm};
      right: ${(props) => props.theme.spacing.sm};
      background-color: ${(props) => props.theme.colour.semantic.success};
      color: ${(props) => props.theme.colour.text.inverse};
      padding: ${(props) => props.theme.spacing.xs}
        ${(props) => props.theme.spacing.sm};
      border-radius: ${(props) => props.theme.border.borderRadiusSmall};
      font-size: ${(props) => props.theme.fonts.textFontSizeS};
      font-weight: 600;
      z-index: 1;

      ${media.tablet`
        padding: 8px 16px;
        font-size: 0.9rem;
      `}

      ${media.desktop`
        padding: 10px 20px;
        font-size: 0.85rem;
      `}

      ${media.wide`
        padding: 12px 24px;
        font-size: 0.8rem;
      `}
    }
  }

  /* Loading skeleton */
  &.loading {
    .structure__image-container {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
    }

    .structure__title,
    .structure__info > li {
      background: #f0f0f0;
      border-radius: 4px;
      height: 1em;
      margin-bottom: ${(props) => props.theme.spacing.sm};
    }
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

export default StructureStyled;
